import axios from "axios";

import { TopMenuItem, Permission, User } from "@/types";
import { Area } from "@/types/imageCropper";
import { PermissionMap } from "@/types/user";
import { PropertyStatus } from "@/types/enums";
import { PropertyImage } from "@/types/properties";
import { StatusType } from "@/components/ui/propertyStatusBadge";

import {
  MAINSAIL_ADMIN_SLUG,
  MAX_EMAIL_LENGTH,
  MAX_NAME_LENGTH,
  PERMISSION_MAPPING,
  subdomainBlockedWords,
} from "./constants";
import { ORGANIZATION_SLUG_REGEX } from "./regex";
import { PUBLIC_PATHS } from "@/router/publicRoutes";

export const filterTopMenuItems = (
  items: TopMenuItem[],
  userPermissions: Permission[]
): TopMenuItem[] => {
  return items.filter((item) => {
    if (!item.requiredPermission) return true;

    // Check if the user has a permission with a matching name
    return userPermissions.some(
      (permission) => permission.name === item.requiredPermission
    );
  });
};

export const isGif = (file: File): boolean => {
  return file.type === "image/gif";
};

export const processImage = async (
  sourceImage: HTMLImageElement,
  pixelCrop: Area | null,
  originalFile: File
): Promise<File> => {
  // If it's a GIF, return the original file
  if (isGif(originalFile)) {
    return originalFile;
  }

  // If no crop area or no context, return original file
  if (!pixelCrop) {
    return originalFile;
  }

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return originalFile;
  }

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    sourceImage,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  const blob = await new Promise<Blob>((resolve) => {
    canvas.toBlob(
      (blob) => {
        resolve(blob || new Blob());
      },
      "image/png",
      1
    );
  });

  return new File([blob], "processed-image.png", { type: "image/png" });
};

export const mapUserPermissions = (user: Partial<User>) => {
  const permissionsMap: PermissionMap = {
    addHomeowner: false,
    editHomeownerDetails: false,
    deactivateHomeownerAccount: false,
    addProperty: false,
    editProperty: false,
    managePropertyEditRequests: false,
    managePropertyDeactivationRequests: false,
    setRevenueGoal: false,
  };

  if (user.permissions && Array.isArray(user.permissions)) {
    user.permissions.forEach((permission: Permission) => {
      const backendPermissionName =
        typeof permission === "string" ? permission : permission.name || "";

      // Find the frontend permission key that maps to this backend permission name
      const frontendPermissionKey = Object.entries(PERMISSION_MAPPING).find(
        ([, value]) => value === backendPermissionName
      )?.[0];

      if (frontendPermissionKey && frontendPermissionKey in permissionsMap) {
        permissionsMap[frontendPermissionKey as keyof PermissionMap] = true;
      }
    });
  }

  return permissionsMap;
};

export const mapPermissionsToBackend = (permissions: PermissionMap) => {
  // Create a reverse mapping for frontend to backend conversion
  const reverseMapping: Record<string, string> = {};
  Object.entries(PERMISSION_MAPPING).forEach(([frontendKey, backendValue]) => {
    reverseMapping[frontendKey] = backendValue;
  });

  return Object.entries(permissions)
    .filter((value) => value[1])
    .map(([key]) => reverseMapping[key] || key);
};

export const truncateText = (text: string, maxLength: number) =>
  text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;

export const truncateName = (name: string) => {
  if (!name) return "N/A";
  return truncateText(name, MAX_NAME_LENGTH);
};

export const truncateEmail = (email: string) => {
  if (!email) return "N/A";
  return truncateText(email, MAX_EMAIL_LENGTH);
};

// Helper function to generate dynamic bedroom fields
export const generateBedroomFields = (count = 3) => {
  const bedroomFields: Record<string, string | number | boolean> = {};

  for (let i = 1; i <= count; i++) {
    bedroomFields[`bedroom${i}Type`] = "";
    bedroomFields[`bedroom${i}Quantity`] = 1;
    bedroomFields[`bedroom${i}Type2`] = "";
    bedroomFields[`bedroom${i}Quantity2`] = 1;
    bedroomFields[`bedroom${i}Info`] = "";
  }

  return bedroomFields;
};

// Helper function to generate dynamic bathroom fields
export const generateBathroomFields = (fullCount = 1, halfCount = 1) => {
  const bathroomFields: Record<string, string> = {};

  for (let i = 1; i <= fullCount; i++) {
    bathroomFields[`fullBathroom${i}Type`] = "";
    bathroomFields[`fullBathroom${i}Info`] = "";
  }

  for (let i = 1; i <= halfCount; i++) {
    bathroomFields[`halfBathroom${i}Type`] = "";
    bathroomFields[`halfBathroom${i}Info`] = "";
  }

  return bathroomFields;
};

export const convertToFullMonthName = (
  monthStr: string,
  yearStr: string
): string => {
  // Create a date object
  const date = new Date(`${monthStr} 1, ${yearStr}`);

  return date.toLocaleString("en-US", { month: "long" });
};

export const getDisplayStatus = (status: string): StatusType => {
  const statusUpper = status?.toUpperCase() || "";

  switch (statusUpper) {
    case PropertyStatus.ACTIVE:
      return "active";
    case PropertyStatus.REVIEWING:
      return "reviewing";
    case PropertyStatus.ARCHIVED:
      return "archived";
    case PropertyStatus.INACTIVE:
      return "inactive";
    case PropertyStatus.NON_RENTING:
      return "nonRenting";
    case PropertyStatus.CREATE_LISTING:
    case PropertyStatus.PHOTOS_AND_VIDEO:
    case PropertyStatus.CREATE_CALENDAR_BLOCK:
    case PropertyStatus.PENDING_ACTION:
      return "pending";
    default:
      return "pending";
  }
};

export const formatLightBoxList = (images: PropertyImage[]) =>
  images.map((image) => ({
    src: image.url,
  }));

export const getCalendarYear = (numYears: number = 6): string[] => {
  const currentDate = new Date();
  const currentYearNum = currentDate.getFullYear();
  return Array.from({ length: numYears }, (_, i) =>
    (currentYearNum + i).toString()
  );
};

/**
 * Normalises a thrown value into a user-facing message. Rejected thunks
 * surface a string; anything else may throw an Error. Blank messages fall
 * through to the caller's default.
 */
export const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === "string" && error) return error;
  if (error instanceof Error && error.message) return error.message;

  return fallback;
};

export const handleApiError = (error: unknown, defaultMessage: string) => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error?.message || defaultMessage;
  }
  return defaultMessage;
};

export const getPageNumbers = (currentPage: number, totalPages: number) => {
  if (!totalPages || totalPages === 1) return [];

  const pageNumbers = [];
  const MAX_VISIBLE_PAGES = 3;

  // Always show first page
  pageNumbers.push(1);

  let startPage = Math.max(2, currentPage - 1);
  let endPage = Math.min(totalPages - 1, currentPage + 1);

  // Handle cases near the start
  if (currentPage <= 2) {
    endPage = Math.min(totalPages - 1, MAX_VISIBLE_PAGES);
  }
  // Handle cases near the end
  if (currentPage >= totalPages - 2) {
    startPage = Math.max(2, totalPages - MAX_VISIBLE_PAGES + 1);
  }

  // Add ellipsis after first page if needed
  if (startPage > 2) {
    pageNumbers.push("...");
  }

  // Add middle pages
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  // Add ellipsis before last page if needed
  if (endPage < totalPages - 1) {
    pageNumbers.push("...");
  }

  // Add last page if we have more than one page
  if (totalPages > 1) {
    pageNumbers.push(totalPages);
  }

  return pageNumbers;
};

export const getPropertyColor = (propertyId: string): string => {
  let hash = 0;
  for (let i = 0; i < propertyId.length; i++) {
    hash = propertyId.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 70%, 50%)`;
};

export const isValidSubdomain = (name: string): boolean => {
  if (!ORGANIZATION_SLUG_REGEX.test(name)) return false;
  if (subdomainBlockedWords.includes(name.toLowerCase())) return false;
  return true;
};

/**
 * Resolve where a user should log in after setting their password.
 *
 * Org users set their password on their org subdomain, where the hostname slug
 * resolves to an organization — they log in on the same origin (SPA nav). Global
 * (MainSail) admins set their password at the ROOT origin (no org subdomain), so
 * no organization resolves and `organizationSlug` is empty. They authenticate
 * through the MainSail admin org subdomain, so this returns an absolute URL to
 * that subdomain, which requires a full (cross-origin) navigation.
 */
export const getLoginDestination = (
  organizationSlug: string
): { path: string; external: boolean } => {
  const { protocol, hostname, host } = window.location;
  const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1";

  // A resolved org (or local dev, which uses the fallback org) logs in in place.
  if (organizationSlug || isLocalHost) {
    return { path: PUBLIC_PATHS.LOGIN, external: false };
  }

  // Root origin with no org → global admin → send to the admin org subdomain.
  return {
    path: `${protocol}//${MAINSAIL_ADMIN_SLUG}.${host}${PUBLIC_PATHS.LOGIN}`,
    external: true,
  };
};
